import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  AvatarGroup,
  Chip,
  Stack,
  MenuItem,
  Select,
  Button,
  IconButton,
  Menu,
  InputAdornment,
  TextField,
  Skeleton,
  Tooltip,
  Divider,
  alpha,
} from '@mui/material';
import {
  MoreVert,
  Add,
  Assignment,
  Group as GroupIcon,
  Draw,
  Visibility,
  Search,
  LocationOn,
  FilterAltOff,
  TrendingUp,
  CheckCircle,
  AccessTime,
  Inbox,
  HomeWork,
  Apartment,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useMemo, useState, MouseEvent, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useListInvestments } from '@/lib/api/investments.ts';
import { useGetMe } from '@/lib/api/me.ts';
import { useListCollaborations } from '@/lib/api/collaborations.ts';
import { CollaborationContractModal } from '@/features/collaborations/CollaborationContractModal.tsx';
import { bothHaveSigned, hasUserSigned } from '@/features/collaborations/list/helpers.ts';
import { canCreateCoInvestment } from '@/features/investments/co-investment/access';
import { formatPercentage } from '@/utils/misc.ts';
import type { InvestmentPhase, ListPsiCollaborationsDTO } from '@inversor-hub/types';
import { SPAIN_PROVINCES } from '@/utils/provinces.ts';
import useIsMobile from '@/hooks/useIsMobile';
import { PHASE_CONFIG, PHASE_ORDER } from '../checklist/checklist-constants';
import { PageHeader } from '@/components/ui/PageHeader.tsx';

type ContractState = 'none' | 'awaiting-psi' | 'awaiting-investor' | 'signed';

const getContractState = (collaboration: ListPsiCollaborationsDTO | undefined): ContractState => {
  if (!collaboration) return 'none';
  if (!collaboration.psiSigned) return 'awaiting-psi';
  if (!collaboration.investorSigned) return 'awaiting-investor';
  return 'signed';
};

const PsiInvestmentsList = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile('lg');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPhase, setSelectedPhase] = useState<string>('');
  const [selectedProvince, setSelectedProvince] = useState('');
  const [selectedInvestor, setSelectedInvestor] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedInvestmentId, setSelectedInvestmentId] = useState<string | null>(null);
  const [openCollaborationContractId, setOpenCollaborationContractId] = useState<string | null>(
    null
  );

  const { data: currentUser } = useGetMe();
  const { data: investments, isLoading } = useListInvestments<'psi'>();
  const { data: collaborations = [], isLoading: isLoadingCollaborations } =
    useListCollaborations<'psi'>();
  const canShowCreateCoInvestmentButton = canCreateCoInvestment(currentUser);
  const isPageLoading = isLoading || isLoadingCollaborations;

  const investorsMap = useMemo(
    () =>
      new Map(
        investments?.flatMap((investment) =>
          investment.investor ? [[investment.investor.id, investment.investor] as const] : []
        )
      ),
    [investments]
  );
  const collaborationsByOpportunityId = useMemo(
    () =>
      new Map(collaborations.map((collaboration) => [collaboration.opportunityId, collaboration])),
    [collaborations]
  );

  const filteredInvestments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return (investments || []).filter((inv) => {
      if (selectedPhase && inv.currentPhase !== selectedPhase) return false;
      if (selectedProvince && inv.province !== selectedProvince) return false;
      if (selectedInvestor && inv.investor?.id !== selectedInvestor) return false;
      if (term) {
        const haystack = [inv.alias, inv.address, inv.city, inv.investor?.name]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [investments, searchTerm, selectedInvestor, selectedPhase, selectedProvince]);

  const stats = useMemo(() => {
    const list = investments || [];
    const inProgress = list.filter((i) => i.currentPhase !== 'COMPLETE').length;
    const coInvestments = list.filter((i) => i.isCoInvestment).length;
    const pendingContracts = collaborations.filter((c) => !c.psiSigned).length;
    return {
      total: list.length,
      inProgress,
      coInvestments,
      pendingContracts,
    };
  }, [investments, collaborations]);

  const hasActiveFilters = Boolean(
    searchTerm || selectedPhase || selectedProvince || selectedInvestor
  );
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedPhase('');
    setSelectedProvince('');
    setSelectedInvestor('');
  };

  const selectedCollaboration = openCollaborationContractId
    ? collaborations.find((collaboration) => collaboration.id === openCollaborationContractId)
    : undefined;

  const menuInvestment = selectedInvestmentId
    ? investments?.find((inv) => inv.id === selectedInvestmentId)
    : undefined;
  const menuCollaboration = menuInvestment?.opportunityId
    ? collaborationsByOpportunityId.get(menuInvestment.opportunityId)
    : undefined;

  const handleMenuOpen = (event: MouseEvent<HTMLElement>, investmentId: string) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedInvestmentId(investmentId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedInvestmentId(null);
  };

  const handleViewDetails = () => {
    if (selectedInvestmentId) {
      navigate(`/inversiones/${selectedInvestmentId}`);
    }
    handleMenuClose();
  };

  const handleViewChecklist = (investmentId: string) => {
    navigate(`/inversiones/${investmentId}/checklist`);
  };

  const handlePrimaryAction = (investmentId: string, hasChecklist: boolean) => {
    if (hasChecklist) {
      handleViewChecklist(investmentId);
      return;
    }
    navigate(`/inversiones/${investmentId}`);
  };

  const navigateToInvestment = (investmentId: string) => {
    navigate(`/inversiones/${investmentId}`);
  };

  const getPhaseLabel = (phase: InvestmentPhase) => {
    if (phase === 'COMPLETE') return 'Completado';
    return PHASE_CONFIG[phase]?.label || phase;
  };

  const getPhaseColor = (phase: InvestmentPhase) => {
    if (phase === 'COMPLETE') return '#388e3c';
    return PHASE_CONFIG[phase]?.color || '#757575';
  };

  const PhaseChip = ({ phase }: { phase: InvestmentPhase }) => {
    const color = getPhaseColor(phase);
    const label = getPhaseLabel(phase);
    const PhaseIcon = phase !== 'COMPLETE' ? PHASE_CONFIG[phase]?.icon : CheckCircle;
    return (
      <Chip
        size="small"
        icon={
          PhaseIcon ? <PhaseIcon sx={{ fontSize: 14, color: `${color} !important` }} /> : undefined
        }
        label={label}
        sx={{
          bgcolor: alpha(color, 0.12),
          color: color,
          fontWeight: 600,
          border: `1px solid ${alpha(color, 0.25)}`,
          '& .MuiChip-icon': { ml: 0.75 },
        }}
      />
    );
  };

  const ReturnDisplay = ({ value }: { value?: number }) => {
    if (value == null) {
      return (
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
      );
    }
    const color =
      value >= 0.08 ? 'success.main' : value >= 0.05 ? 'warning.dark' : 'text.secondary';
    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <TrendingUp sx={{ fontSize: 16, color }} />
        <Typography variant="body2" fontWeight={600} color={color}>
          {formatPercentage(value)}
        </Typography>
      </Stack>
    );
  };

  const ContractStatusCell = ({
    collaboration,
    compact = false,
  }: {
    collaboration: ListPsiCollaborationsDTO | undefined;
    compact?: boolean;
  }) => {
    const state = getContractState(collaboration);

    if (state === 'none') {
      return (
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
      );
    }

    if (state === 'awaiting-psi') {
      return (
        <Button
          variant="contained"
          color="primary"
          startIcon={<Draw />}
          onClick={() => setOpenCollaborationContractId(collaboration!.id)}
          size="small"
          fullWidth={compact}
          sx={{ minWidth: 130 }}
        >
          Firmar
        </Button>
      );
    }

    if (state === 'awaiting-investor') {
      return (
        <Tooltip title="El inversor todavía no ha firmado el contrato">
          <Chip
            size="small"
            icon={<AccessTime sx={{ fontSize: 14 }} />}
            label="Pendiente inversor"
            sx={{
              bgcolor: 'warning.50',
              color: 'warning.dark',
              fontWeight: 600,
              border: (t) => `1px solid ${alpha(t.palette.warning.main, 0.3)}`,
              width: compact ? '100%' : 'auto',
            }}
          />
        </Tooltip>
      );
    }

    return (
      <Tooltip title="Ver contrato firmado">
        <Chip
          size="small"
          clickable
          onClick={() => setOpenCollaborationContractId(collaboration!.id)}
          icon={<CheckCircle sx={{ fontSize: 14, color: 'success.main !important' }} />}
          label="Firmado"
          sx={{
            bgcolor: 'success.50',
            color: 'success.dark',
            fontWeight: 600,
            border: (t) => `1px solid ${alpha(t.palette.success.main, 0.3)}`,
            width: compact ? '100%' : 'auto',
            '&:hover': {
              bgcolor: (t) => alpha(t.palette.success.main, 0.18),
            },
          }}
        />
      </Tooltip>
    );
  };

  const InvestorCell = ({ inv }: { inv: NonNullable<typeof investments>[number] }) => {
    if (inv.isCoInvestment && inv.coInvestors && inv.coInvestors.length > 0) {
      const names = inv.coInvestors.map((c) => c.name).join(', ');
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title={names}>
            <AvatarGroup
              max={3}
              sx={{
                '& .MuiAvatar-root': {
                  width: 28,
                  height: 28,
                  fontSize: '0.7rem',
                  border: '2px solid',
                  borderColor: 'background.paper',
                },
              }}
            >
              {inv.coInvestors.map((ci) => (
                <Avatar key={ci.id} src={ci.photoUrl} alt={ci.name}>
                  {ci.name?.charAt(0)}
                </Avatar>
              ))}
            </AvatarGroup>
          </Tooltip>
          <Chip
            icon={<GroupIcon sx={{ fontSize: 13 }} />}
            label="Co-inversión"
            size="small"
            sx={{
              bgcolor: 'secondary.main',
              color: 'common.white',
              fontWeight: 600,
              fontSize: '0.7rem',
              '& .MuiChip-icon': { color: 'common.white' },
            }}
          />
        </Stack>
      );
    }
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Avatar src={inv.investor?.photoUrl} sx={{ width: 28, height: 28, fontSize: '0.8rem' }}>
          {inv.investor?.name?.charAt(0) ?? 'S'}
        </Avatar>
        <Typography variant="body2" fontWeight={500} noWrap>
          {inv.investor?.name ?? 'Sin inversor'}
        </Typography>
      </Stack>
    );
  };

  const renderTableRow = (inv: NonNullable<typeof investments>[number]) => {
    const collaboration = inv.opportunityId
      ? collaborationsByOpportunityId.get(inv.opportunityId)
      : undefined;

    return (
      <TableRow
        key={inv.id}
        hover
        onClick={() => navigateToInvestment(inv.id)}
        sx={{
          cursor: 'pointer',
          transition: 'background-color 120ms ease',
          '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.04) },
        }}
      >
        <TableCell>
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography fontWeight={600} noWrap>
              {inv.alias || inv.address}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
              <LocationOn sx={{ fontSize: 12, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                {inv.address}
              </Typography>
            </Stack>
          </Stack>
        </TableCell>
        <TableCell>
          <InvestorCell inv={inv} />
        </TableCell>
        <TableCell>
          <PhaseChip phase={inv.currentPhase} />
        </TableCell>
        <TableCell align="right">
          <ReturnDisplay value={inv.grossReturn} />
        </TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          <ContractStatusCell collaboration={collaboration} />
        </TableCell>
        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Acciones">
            <IconButton size="small" onClick={(e) => handleMenuOpen(e, inv.id)}>
              <MoreVert />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  const renderMobileCard = (inv: NonNullable<typeof investments>[number]) => {
    const collaboration = inv.opportunityId
      ? collaborationsByOpportunityId.get(inv.opportunityId)
      : undefined;

    return (
      <Paper
        key={inv.id}
        elevation={0}
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          cursor: 'pointer',
          transition: 'all 120ms ease',
          '&:hover': { borderColor: 'primary.main', boxShadow: 1 },
        }}
        onClick={() => navigateToInvestment(inv.id)}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1.5} mb={1.5}>
          <Avatar
            variant="rounded"
            sx={{
              bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
              color: 'primary.main',
              width: 40,
              height: 40,
            }}
          >
            <HomeWork fontSize="small" />
          </Avatar>
          <Box flex={1} minWidth={0}>
            <Typography fontWeight={600} noWrap>
              {inv.alias || inv.address}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <LocationOn sx={{ fontSize: 13, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                {inv.address}
              </Typography>
            </Stack>
          </Box>
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, inv.id)}>
            <MoreVert />
          </IconButton>
        </Stack>

        <Box mb={1.5}>
          <InvestorCell inv={inv} />
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          mb={2}
        >
          <PhaseChip phase={inv.currentPhase} />
          <ReturnDisplay value={inv.grossReturn} />
        </Stack>

        <Divider sx={{ mb: 1.5 }} />

        <Stack spacing={1} onClick={(e) => e.stopPropagation()}>
          <Button
            variant={inv.hasChecklist ? 'contained' : 'outlined'}
            startIcon={inv.hasChecklist ? <Assignment /> : undefined}
            onClick={() => handlePrimaryAction(inv.id, inv.hasChecklist)}
            fullWidth
            size="small"
          >
            {inv.hasChecklist ? 'Ver itinerario' : 'Ver detalles'}
          </Button>
          {collaboration && <ContractStatusCell collaboration={collaboration} compact />}
        </Stack>
      </Paper>
    );
  };

  const renderSkeletonRows = () =>
    Array.from({ length: 4 }).map((_, i) => (
      <TableRow key={`sk-${i}`}>
        {Array.from({ length: 6 }).map((__, j) => (
          <TableCell key={j}>
            <Skeleton variant="text" height={32} />
          </TableCell>
        ))}
      </TableRow>
    ));

  const renderEmptyState = () => (
    <Stack alignItems="center" spacing={2} py={6} px={3}>
      <Avatar
        sx={{
          bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
          color: 'primary.main',
          width: 64,
          height: 64,
        }}
      >
        <Inbox sx={{ fontSize: 32 }} />
      </Avatar>
      <Stack alignItems="center" spacing={0.5}>
        <Typography fontWeight={600}>
          {hasActiveFilters ? 'Sin resultados' : 'Aún no hay operaciones'}
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth={420}>
          {hasActiveFilters
            ? 'Ajusta o limpia los filtros para ver más resultados.'
            : 'Registra tu primera operación o gestiona las que surjan de tus oportunidades con inversores.'}
        </Typography>
      </Stack>
      {hasActiveFilters ? (
        <Button variant="text" startIcon={<FilterAltOff />} onClick={clearFilters} size="small">
          Limpiar filtros
        </Button>
      ) : (
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/inversiones/nueva')}
        >
          Registrar operación
        </Button>
      )}
    </Stack>
  );

  const StatCard = ({
    label,
    value,
    icon,
    accent,
    isLoading: loading,
  }: {
    label: string;
    value: number;
    icon: ReactNode;
    accent: string;
    isLoading?: boolean;
  }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        flex: 1,
        minWidth: 0,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <Avatar
        variant="rounded"
        sx={{
          bgcolor: alpha(accent, 0.12),
          color: accent,
          width: 42,
          height: 42,
        }}
      >
        {icon}
      </Avatar>
      <Box minWidth={0}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {label}
        </Typography>
        {loading ? (
          <Skeleton width={48} height={28} />
        ) : (
          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
            {value}
          </Typography>
        )}
      </Box>
    </Paper>
  );

  return (
    <Box>
      <PageHeader
        title="Operaciones"
        subtitle="Gestiona el ciclo completo de cada operación que llevas para tus inversores."
        action={
          <Stack direction="row" spacing={1.5}>
            {canShowCreateCoInvestmentButton && (
              <Link to="/inversiones/nueva-coinversion" style={{ textDecoration: 'none' }}>
                <Button variant="outlined" startIcon={<GroupIcon />}>
                  Nueva co-inversión
                </Button>
              </Link>
            )}
            <Link to="/inversiones/nueva" style={{ textDecoration: 'none' }}>
              <Button variant="contained" startIcon={<Add />}>
                Nueva operación
              </Button>
            </Link>
          </Stack>
        }
      />

      {/* KPI Strip */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
        <StatCard
          label="Total operaciones"
          value={stats.total}
          icon={<Apartment fontSize="small" />}
          accent="#0E8D9D"
          isLoading={isPageLoading}
        />
        <StatCard
          label="En curso"
          value={stats.inProgress}
          icon={<AccessTime fontSize="small" />}
          accent="#1976d2"
          isLoading={isPageLoading}
        />
        <StatCard
          label="Co-inversiones"
          value={stats.coInvestments}
          icon={<GroupIcon fontSize="small" />}
          accent="#003B5C"
          isLoading={isPageLoading}
        />
        <StatCard
          label="Contratos por firmar"
          value={stats.pendingContracts}
          icon={<Draw fontSize="small" />}
          accent="#E88820"
          isLoading={isPageLoading}
        />
      </Stack>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <TextField
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por dirección, alias o inversor..."
            size="small"
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
          <Select
            value={selectedPhase}
            onChange={(e) => setSelectedPhase(e.target.value)}
            size="small"
            displayEmpty
            sx={{ minWidth: 160 }}
            renderValue={(value) =>
              value ? getPhaseLabel(value as InvestmentPhase) : 'Todas las fases'
            }
          >
            <MenuItem value="">Todas las fases</MenuItem>
            {[...PHASE_ORDER, 'COMPLETE'].map((phase) => (
              <MenuItem key={phase} value={phase}>
                {getPhaseLabel(phase as InvestmentPhase)}
              </MenuItem>
            ))}
          </Select>
          <Select
            value={selectedProvince}
            onChange={(e) => setSelectedProvince(e.target.value)}
            size="small"
            displayEmpty
            sx={{ minWidth: 160 }}
            renderValue={(value) => (value ? (value as string) : 'Todas las provincias')}
          >
            <MenuItem value="">Todas las provincias</MenuItem>
            {SPAIN_PROVINCES.map((prov) => (
              <MenuItem key={prov} value={prov}>
                {prov}
              </MenuItem>
            ))}
          </Select>
          <Select
            value={selectedInvestor}
            onChange={(e) => setSelectedInvestor(e.target.value)}
            size="small"
            displayEmpty
            sx={{ minWidth: 180 }}
            renderValue={(value) => {
              if (!value) return 'Todos los inversores';
              const investor = investorsMap.get(value as string);
              return investor?.name ?? 'Inversor';
            }}
          >
            <MenuItem value="">Todos los inversores</MenuItem>
            {[...investorsMap.values()].map((inv) => (
              <MenuItem key={inv.id} value={inv.id}>
                {inv.name}
              </MenuItem>
            ))}
          </Select>
          {hasActiveFilters && (
            <Button
              variant="text"
              color="inherit"
              startIcon={<FilterAltOff />}
              onClick={clearFilters}
              size="small"
            >
              Limpiar
            </Button>
          )}
        </Stack>
        {!isPageLoading && (
          <Typography variant="caption" color="text.secondary" mt={1.5} display="block">
            {filteredInvestments.length}{' '}
            {filteredInvestments.length === 1 ? 'operación' : 'operaciones'}
            {hasActiveFilters && investments ? ` de ${investments.length}` : ''}
          </Typography>
        )}
      </Paper>

      {/* Table / Cards */}
      {!isMobile ? (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
        >
          <Table
            size="small"
            sx={{
              minWidth: 720,
              '& .MuiTableCell-root': { py: 1.25, px: 1.5 },
              '& .MuiTableCell-root:first-of-type': { pl: 2 },
              '& .MuiTableCell-root:last-of-type': { pr: 2 },
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    bgcolor: 'grey.50',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    borderBottom: '1px solid',
                    borderBottomColor: 'divider',
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <TableCell>Propiedad</TableCell>
                <TableCell sx={{ width: 180 }}>Inversor</TableCell>
                <TableCell sx={{ width: 150 }}>Fase</TableCell>
                <TableCell sx={{ width: 90 }} align="right">
                  Rent.
                </TableCell>
                <TableCell sx={{ width: 160 }}>Contrato</TableCell>
                <TableCell sx={{ width: 90 }} align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {isPageLoading ? (
                renderSkeletonRows()
              ) : filteredInvestments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ borderBottom: 'none' }}>
                    {renderEmptyState()}
                  </TableCell>
                </TableRow>
              ) : (
                filteredInvestments.map((inv) => renderTableRow(inv))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Stack spacing={2}>
          {isPageLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={220} />
            ))
          ) : filteredInvestments.length === 0 ? (
            <Paper
              elevation={0}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
            >
              {renderEmptyState()}
            </Paper>
          ) : (
            filteredInvestments.map((inv) => renderMobileCard(inv))
          )}
        </Stack>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          paper: { sx: { minWidth: 240, borderRadius: 2 } },
        }}
      >
        <MenuItem onClick={handleViewDetails}>
          <Visibility fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
          Ver detalles
        </MenuItem>
        {menuInvestment?.hasChecklist && (
          <MenuItem
            onClick={() => {
              if (selectedInvestmentId) handleViewChecklist(selectedInvestmentId);
              handleMenuClose();
            }}
          >
            <Assignment fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
            Ver itinerario
          </MenuItem>
        )}
        {menuCollaboration && (
          <MenuItem
            onClick={() => {
              setOpenCollaborationContractId(menuCollaboration.id);
              handleMenuClose();
            }}
          >
            <Draw fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
            Ver contrato
          </MenuItem>
        )}
      </Menu>

      {openCollaborationContractId && currentUser && (
        <CollaborationContractModal
          open={true}
          onClose={() => setOpenCollaborationContractId(null)}
          collaborationId={openCollaborationContractId}
          userRole="psi"
          hasUserSignature={Boolean(currentUser.signature?.trim())}
          hasUserSigned={hasUserSigned(selectedCollaboration, 'psi')}
          bothHaveSigned={bothHaveSigned(selectedCollaboration)}
        />
      )}
    </Box>
  );
};

export default PsiInvestmentsList;
